import { format, formatDistanceToNow } from 'date-fns';
import { AlertCircle, CheckCircle, Clock, Copy, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTransferJobs } from '@/hooks/use-transfer-jobs';
import { TransferJob } from '@/types/transfer-job';

interface TransferJobCardProps {
  job: TransferJob;
}

export function TransferJobCard({ job }: TransferJobCardProps) {
  const { t } = useTranslation();
  const { deleteJob } = useTransferJobs();

  const formatDate = (date: Date) => {
    return format(new Date(date), 'MMM dd, HH:mm');
  };

  const formatDuration = (start: Date, end?: Date) => {
    const endTime = end ? new Date(end).getTime() : Date.now();
    const duration = endTime - new Date(start).getTime();
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusIcon = (status: TransferJob['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'active':
        return <AlertCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'failed':
        return <XCircle className="h-4 w-4" />;
    }
  };

  const getStatusVariant = (status: TransferJob['status']) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'active':
        return 'default';
      case 'completed':
        return 'default';
      case 'failed':
        return 'destructive';
    }
  };

  const getStatusText = (status: TransferJob['status']) => {
    return t(`common.${status}` as any);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const truncateAddress = (address: string, start = 6, end = 4) => {
    if (address.length <= start + end) return address;
    return `${address.slice(0, start)}...${address.slice(-end)}`;
  };

  return (
    <Card className="transition-all hover:shadow-md p-0">
      <CardContent className="p-4">
        <div className="flex-1 space-y-3">
          {/* Header with Job ID and Status */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {getStatusIcon(job.status)}
              <Badge variant={getStatusVariant(job.status)}>
                {getStatusText(job.status)}
              </Badge>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
              <span className="font-inter break-words break-all">{truncateAddress(job.jobId)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => copyToClipboard(job.jobId)}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>

            <div className="flex-1"></div>

            <Button
              variant="destructive"
              size="sm"
              className="flex items-center gap-2"
              onClick={() => {
                if (confirm(t('confirmations.deleteRecordConfirm'))) {
                  deleteJob(job.jobId);
                }
              }}
            >
              <XCircle className="h-3 w-3" />
              {t('common.deleteRecord')}
            </Button>
          </div>

          {/* Transfer Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="min-w-0">
              <div className="text-muted-foreground">
                {t('transfer.fromChain')}
              </div>
              <div className="font-medium capitalize">{job.fromChain}</div>
              <div className="text-xs text-muted-foreground font-mono break-words break-all">
                {truncateAddress(job.fromAddress)}
              </div>
            </div>

            <div className="min-w-0">
              <div className="text-muted-foreground">
                {t('transfer.toChain')}
              </div>
              <div className="font-medium capitalize">{job.toChain}</div>
              <div className="text-xs text-muted-foreground font-mono break-words break-all">
                {truncateAddress(job.toAddress)}
              </div>
            </div>

            <div>
              <div className="text-muted-foreground">{t('common.amount')}</div>
              <div className="font-medium">{job.amount} USDT</div>
            </div>
          </div>

          {/* Timestamps and Duration */}
          <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
            <div className="min-w-0 break-words break-all">
              {t('transfer.started')}: {formatDate(job.createdAt)}
              <span className="ml-1">
                (
                {formatDistanceToNow(new Date(job.createdAt), {
                  addSuffix: true,
                })}
                )
              </span>
            </div>

            {job.completedAt && (
              <div className="shrink-0">
                {t('transfer.duration')}:{' '}
                {formatDuration(job.createdAt, job.completedAt)}
              </div>
            )}

            {job.completedAt && (
              <div className="min-w-0 break-words break-all">
                {t('transfer.completed')}: {formatDate(job.completedAt)}
              </div>
            )}
          </div>

          {/* Error Message */}
          {job.error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                <div className="text-sm text-red-700 dark:text-red-300 break-words break-all min-w-0">
                  {job.error}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
